import { useState } from "react"
import Popover from "@/v2-ui/popover"
import Textarea from "@/v2-ui/textarea"
import Label from "@/v2-ui/label"
import { errorToast } from "@/v2-ui/toast"
import Button, {
  TooltipButton,
  type TooltipButtonProps,
} from "@/v2-backoffice-client/components/button"
import { useLayerEnableManyMutation } from "@/v2-backoffice-client/layer/enable/many/layer.enable.many.mutation"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"

export type LayerReviewRejectButtonProps = TooltipButtonProps & {
  layers: LayerFragment[]
  onDone?(): void
}

/**
 * Rejects one or more pending layers: keeps them disabled for customers and
 * stamps the reviewer (plus an optional note explaining why), which removes
 * them from the review queue. The whole selection goes through one batch
 * mutation, with the note applied to every layer. A rejected layer can still
 * be published later via the normal enable toggle.
 *
 * The note form lives in a popover anchored to the button (same pattern as
 * ConfirmButton) — a full dialog is overkill for one optional field.
 */
function LayerReviewRejectButton(props: LayerReviewRejectButtonProps) {
  const { layers, onDone, children, ...buttonProps } = props

  const [isOpen, setIsOpen] = useState(false)
  const [note, setNote] = useState("")

  const [toggle, { loading, error }] = useLayerEnableManyMutation({
    refetchQueries: [LAYER_COUNTS_QUERY_NAME],
  })
  errorToast(error, {
    title: "Failed to reject layer",
  })

  const count = layers.length

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setNote("")
      }}
    >
      <Popover.Anchor asChild>
        <TooltipButton
          variant="destructive"
          size="sm"
          iconSrc="eye-crossed"
          isLoading={loading}
          content="Keep hidden from customers and remove from the review queue"
          // Keep the tooltip mounted (stable anchor for popover positioning)
          // but force it closed while the note form is open.
          open={isOpen ? false : undefined}
          onClick={() => {
            setIsOpen(true)
          }}
          {...buttonProps}
        >
          {children ?? "Reject"}
        </TooltipButton>
      </Popover.Anchor>

      <Popover.Content className="w-80" side="bottom" align="end">
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {count === 1
              ? `\u201C${layers[0]?.Name}\u201D stays hidden from customers and leaves the review queue.`
              : `The ${count} selected layers stay hidden from customers and leave the review queue.`}{" "}
            You can still take them live later from the Layers tab.
          </p>

          <div className="layer-review-reject-note space-y-2">
            <Label htmlFor="layer-review-reject-note">Note (optional)</Label>
            <Textarea
              id="layer-review-reject-note"
              placeholder="Why is this layer not ready? E.g. broken styling, missing translations, bad data..."
              value={note}
              onChange={(e) => {
                setNote(e.target.value)
              }}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsOpen(false)
                setNote("")
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              isLoading={loading}
              onClick={async () => {
                await toggle({
                  variables: {
                    featureCodes: layers.map((layer) => layer.FeatureCode),
                    isEnabled: false,
                    note: note.trim() || undefined,
                  },
                })
                setIsOpen(false)
                setNote("")
                onDone?.()
              }}
            >
              {count === 1 ? "Reject layer" : `Reject ${count} layers`}
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  )
}

export default LayerReviewRejectButton
